/**
 * Открыть ролик В СИСТЕМНОМ ОКНЕ БРАУЗЕРА поверх приложения (`Browser.open`: SFSafariViewController на iOS,
 * Custom Tabs на Android).
 *
 * ⚠️ ПОЧЕМУ НЕ ПРОСТО РАМКА НА СТРАНИЦЕ. YouTube отвергает внутренний адрес
 * приложения и отвечает «Ошибка 153»: его программному интерфейсу нужен
 * настоящий http(s)-источник, а получить такой у главного окна на iOS
 * НЕЛЬЗЯ — систему нельзя попросить обслуживать `https` самой, и Capacitor
 * молча возвращает `capacitor://` (проверено по исходникам 04.09.2026, см.
 * пояснение в capacitor.config.ts). Проверено и стоило сломанной сборки 48.
 *
 * ⚠️ ПОЧЕМУ БОЛЬШЕ НЕ `@capacitor/inappbrowser` (12.10.2026, сборка 1.1.2). Его окно было без строки адреса, но
 * плагин тащил `OSInAppBrowserLib` на SwiftUI, и Xcode 26 прописывал приложению ОБЯЗАТЕЛЬНУЮ связь с
 * `SwiftUICore` — её нет до iOS 18. 1.1.0 и 1.1.1 на iOS 15–17 не запускались вовсе: чёрный экран, вылет через
 * секунду (решение техлида, техдолг — «Падение на iOS 15–17»). Сторож теперь в `scripts/ios-min-os-guard.sh`.
 * Не возвращать плагин и не подключать другой на SwiftUI без проверки сторожем.
 *
 * ⚠️ СТРОКА АДРЕСА ВИДНА — ПРИНЯТО (техлид, 12.10.2026). Решение владельца 14.09.2026 (Apple 3.1.3(f)) запрещало
 * окно со строкой адреса. На iOS это окно теперь — АВАРИЙНЫЙ путь: ролик урока играет в нашем `InlineVideo`
 * (`lib/inlineVideoNative.ts`), сюда попадаем только при `features.video_native = false` с сервера. На Android —
 * основной путь (там `InlineVideo` нет). Поэтому в строке адреса не сайт, а `video.onebilim.kz` (`VIDEO_ORIGIN`
 * ниже, решение владельца 12.10.2026): там нет ничего, кроме плеера, и из окна до цен не дойти.
 *
 * Внутри открывается НАША страница плеера с настоящего адреса: свои кнопки, логотип YouTube скрыт. Окно своё у
 * системы, приложение не перезапускается и Face ID заново не спрашивает. Кнопку закрытия («Готово») рисует система
 * на языке телефона. Автозапуск со звуком Safari может не разрешить — тогда ученик нажимает ▶ ещё раз.
 *
 * ⚠️ ЗАПАСНОГО ВЫХОДА НАРУЖУ НЕТ: сбой окна — ошибка, вызывающий показывает «Видео не загрузилось, попробуйте ещё
 * раз». `window.open` в Safari не добавлять.
 */
export async function openVideoOutside(url: string): Promise<void> {
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url, presentationStyle: 'fullscreen' });
}

/**
 * Домен плеера — и для окна ролика (`openVideoOutside`: Android — каждый ролик, iOS — материалы и выключенный
 * `video_native`), и для НАТИВНОГО вида урока iOS (`native = true`, с 1.1.2 — техдолг 401).
 *
 * ⚠️ НАТИВНЫЙ ВИД — ТОЛЬКО С НОВОЙ СБОРКОЙ. Плагин пускает один хост (`allowedHost` в
 * `ios/App/App/InlineVideoPlugin.swift`) и только с него принимает просьбу о полном экране; JS и Swift уезжают
 * одним бандлом, их согласие держит `videoOutside.test.ts`. Сборки 1.1.1 и раньше ходят на academy `/embed/` —
 * её на сервере не выключать, пока они на руках.
 *
 * ⚠️ ОТДЕЛЬНЫЙ ДОМЕН, А НЕ САЙТ (решение владельца 12.10.2026). У окна есть строка адреса и «Открыть в
 * Safari»: с academy проверяющий магазина дошёл бы до цен (3.1.3). На video живы только `/embed/` и её
 * файлы, остальное — 404 (`infrastructure/host-nginx/onebilim.kz.conf`).
 *
 * Своего домена плеера у стенда нет (решение владельца 12.10.2026): приложение стенда открывает окно здесь же.
 * Страница плеера к API не ходит, поэтому для неё стенд и прод неразличимы.
 */
const VIDEO_ORIGIN = 'https://video.onebilim.kz';

/**
 * Собрать адрес страницы плеера.
 *
 * ⚠️ ПОДПИСЬ ЗНАКА ИДЁТ ЯКОРЕМ (`#m=`), А НЕ ПАРАМЕТРОМ. Якорь браузер на сервер
 * НЕ отправляет — почта ученика не попадёт в журналы. Обычный параметр адреса
 * попал бы в каждую строчку журнала nginx.
 */
/**
 * @param captions включать ли субтитры, когда в самом окне выбора ещё нет.
 * @param speed скорость воспроизведения — так же умолчанием для окна.
 *   ⚠️ Обычными параметрами, а не якорем: якорь занят подписью знака ровно
 *   потому, что почта ученика не должна попасть в журналы nginx. Здесь
 *   личного нет — едет открыто.
 * @param native страница открыта нативным видом урока (проба 60, ветка video-native-webview): разворот на весь
 *   экран делает приложение. Страница верит флагу, только если приложение дало канал — `lib/nativeEmbed.ts`.
 */
export function videoPageUrl(
  videoId: string,
  mark?: string,
  autoplay = true,
  noMark = false,
  captions = false,
  speed = 1,
  native = false,
): string {
  // ⚠️ Автозапуск нужен ОТДЕЛЬНОМУ окну: человек уже нажал на обложку, и второе
  // нажатие было бы лишним. А в карточке урока автозапуск вреден — ролик
  // стартовал бы сам при открытии урока, посреди чтения задания.
  const base =
    `${VIDEO_ORIGIN}/embed/?v=${encodeURIComponent(videoId)}` +
    `${autoplay ? '&autoplay=1' : ''}${noMark ? '&nomark=1' : ''}` +
    `${captions ? '&cc=1' : ''}` +
    // Обычную скорость не пишем: параметр, равный умолчанию, только удлиняет
    // адрес и мешает читать его глазами при разборе.
    `${speed !== 1 ? `&rate=${speed}` : ''}` +
    `${native ? '&native=1' : ''}`;
  return mark ? `${base}#m=${encodeURIComponent(mark)}` : base;
}
